import { gql } from 'graphql-tag'

/**
 * Unified data source for the Latest Updates dashboard:
 *   { items, total, byCompany }
 * Replaces the older split queries.
 */
export const LATEST_UPDATES_QUERY = gql`
  query GetLatestUpdates($limit: Int) {
    getLatestUpdates(limit: $limit)
  }
`

export const MARK_CASE_FOLLOWUPS_READ_MUTATION = gql`
  mutation MarkCaseFollowUpsRead($caseId: ID!) {
    markCaseFollowUpsRead(caseId: $caseId)
  }
`

/**
 * Whether a deep-linked case can actually be opened in Followups (not removed,
 * active company the caller belongs to, active lead agency). Drives the "this
 * case can't be shown" notice instead of a silent fallback to another case.
 */
export const FOLLOWUPS_CASE_REACHABLE_QUERY = gql`
  query FollowupsCaseReachable($caseId: ID!) {
    followupsCaseReachable(caseId: $caseId)
  }
`

/**
 * WHERE a deep-linked case lives — `{ reachable, companyId, agencyId, reason }`.
 * The Followups page selects exactly that scope on arrival rather than trusting
 * the URL's cid/aid, which can be stale, missing, or overridden by the nav's own
 * agency-picking rules.
 */
export const FOLLOWUPS_CASE_TARGET_QUERY = gql`
  query FollowupsCaseTarget($caseId: ID!) {
    followupsCaseTarget(caseId: $caseId)
  }
`

/**
 * Current user's case-plan agenda (JSON array of { id, plannedFor, case }).
 */
export const MY_AGENDA_QUERY = gql`
  query GetMyAgenda($from: String, $to: String) {
    getMyAgenda(from: $from, to: $to)
  }
`

/**
 * Team/company agenda for managers (JSON array; each item adds `user`).
 */
export const TEAM_AGENDA_QUERY = gql`
  query GetTeamAgenda($from: String, $to: String) {
    getTeamAgenda(from: $from, to: $to)
  }
`

/**
 * The lead-agency members the caller may "View As" (whole-page). JSON array of
 * { id, name, shortName, label, color, casesCount } — label/color are the
 * user's own display identity; casesCount = active cases ASSIGNED to them.
 * Drives the Follow-Ups nav right-click menu. SysAdmin + LA members only.
 */
export const VIEWABLE_PEERS_QUERY = gql`
  query GetViewablePeers {
    getViewablePeers
  }
`

/**
 * The users a case can be assigned to ("attended by"): active lead-agency
 * members. With `caseId`, only members of that case's lead agency (feeds the
 * reassign picker); without it, everyone in the caller's scope (feeds the
 * /cases assigned-user filter catalog). JSON array of
 * { id, name, shortName, label, color, role }.
 */
export const ASSIGNABLE_USERS_QUERY = gql`
  query GetAssignableUsers($caseId: ID) {
    getAssignableUsers(caseId: $caseId)
  }
`

/**
 * Lightweight active-cases projection for the "Case List/Tags" report: just the
 * name parts + stage tags, for a WHOLE company (pass filters.leadAgencyId: null
 * for all lead agencies). Separate from the store's heavy query so it never
 * disturbs the loaded `activeCases`.
 */
export const CASES_FOR_TAGS_REPORT_QUERY = gql`
  query GetCasesForTagsReport($companyId: ID!, $filters: CaseFiltersInput) {
    getCasesForCompany(companyId: $companyId, filters: $filters) {
      id
      startedAt
      motherName
      fatherName
      otherContactName
      childName
      # The report's "Users" block groups by assignee — without this every
      # case lands under "[Unassigned Cases]" (post-user-tags regression).
      assignedTo {
        id
        label
        shortName
        name
      }
      leadSource {
        id
        name
        shortName
        areaCode
        displayOrder
      }
      tags {
        assignedAt
        tag {
          id
          label
          category
          displayOrder
        }
      }
    }
  }
`

/**
 * Single round-trip nav data for the cases/followups page:
 * companies the caller can see (with active case counts + unread)
 * + lead agencies inside each (only those with active cases)
 * + per-case unread counts for `currentCaseIds`.
 *
 * Designed to be polled every ~25s — keep the projection thin.
 */
export const CASE_NAV_DATA_QUERY = gql`
  query GetCaseNavData(
    $currentCaseIds: [ID!]
    $caseStageCategories: [String!]
    $viewAsUserId: ID
  ) {
    getCaseNavData(
      currentCaseIds: $currentCaseIds
      caseStageCategories: $caseStageCategories
      viewAsUserId: $viewAsUserId
    )
  }
`

/**
 * Current Cases page: a PAGE of cases across all the companies the caller can
 * see, for ONE stage category (active / won / lost), optionally
 * narrowed by stage tags / assigned user + a free-text search. Server-side
 * filtered + paginated (infinite scroll appends pages, stops when a page comes
 * back shorter than `take`). Projection is exactly what `CurrentCaseCard`
 * renders — name parts (the title), company / lead agency, stage tags + the
 * assignee, unread + follow-up counts, the latest note and plan days. The
 * card opens the full case in Followups, so the heavy case detail lives there.
 */
export const CURRENT_CASES_QUERY = gql`
  query GetCasesInScope(
    $caseStageCategory: String
    $caseTagIds: [ID!]
    $assignedUserIds: [ID!]
    $leadAgencyIds: [ID!]
    $companyIds: [ID!]
    $planningDate: String
    $search: String
    $sort: String
    $groupBy: String
    $skip: Int
    $take: Int
  ) {
    getCasesInScope(
      caseStageCategory: $caseStageCategory
      caseTagIds: $caseTagIds
      assignedUserIds: $assignedUserIds
      leadAgencyIds: $leadAgencyIds
      companyIds: $companyIds
      planningDate: $planningDate
      search: $search
      sort: $sort
      groupBy: $groupBy
      skip: $skip
      take: $take
    ) {
      id
      motherName
      fatherName
      otherContactName
      contactCallerId
      childName
      # The other cases in this child's family (empty for almost every row —
      # the virtual short-circuits without a group, and batches when there is
      # one, so asking for it here costs nothing per card).
      siblings
      assignedTo {
        id
        label
        color
        shortName
        name
      }
      leadSource {
        id
        name
        shortName
      }
      company {
        id
        name
        shortName
        navBarTitle
      }
      # The card's "BCBA: … / RBT: …" line (Sandy 2026-11-04).
      bcba { id firstName lastName nickName }
      rbt { id firstName lastName nickName }
      # Batched (one query for the page, not one per card) — same rows and
      # keys, plus the tag's icon the card doesn't read (Sandy 2026-10-05).
      tags: tagLinks
      unreadTrackingCount
      # The note count and the latest note (a take-1 array), batched for the
      # whole page under the same visibility rules (keystone
      # services/case-collections.logic.ts, Sandy 2026-10-05).
      followUpsCount: liveNotesCount
      latestFollowUp: latestNoteLink
      startedAt
      planDays

      # MY plan for this case — carries the day AND the attend-now/later rank
      # the day view groups by.
      myPlan
    }
  }
`

/**
 * The most recent follow-ups of a single case (newest first) — fed to the
 * card's "N Followups" peek lightbox. Lightweight: just what the peek renders.
 */
export const CASE_RECENT_FOLLOWUPS_QUERY = gql`
  query CaseRecentFollowUps($caseId: ID!, $take: Int) {
    followUps(
      where: { case: { id: { equals: $caseId } }, removed: { equals: false } }
      orderBy: [{ occurredAt: desc }, { displayOrder: desc }, { createdAt: desc }, { id: desc }]
      take: $take
    ) {
      id
      notes
      internal
      occurredAt
      createdAt
      displayOrder
      removedAt
      # The decided-case peek renders these with the REAL FollowUpRow, which
      # reads the unread state, the system-note link and the SMS hop.
      readByMe
      readByOthers
      callRecordIdPlain
      chatMessageId
      createdBy {
        id
        name
        shortName
        stylesClasses
      }
    }
  }
`

/**
 * Total number of cases matching the Current Cases filters (ignores
 * pagination). Drives the "Total: X" readout next to the loaded "Showing Y".
 */
export const CURRENT_CASES_COUNT_QUERY = gql`
  query GetCasesInScopeCount(
    $caseStageCategory: String
    $caseTagIds: [ID!]
    $assignedUserIds: [ID!]
    $leadAgencyIds: [ID!]
    $companyIds: [ID!]
    $planningDate: String
    $search: String
  ) {
    getCasesInScopeCount(
      caseStageCategory: $caseStageCategory
      caseTagIds: $caseTagIds
      assignedUserIds: $assignedUserIds
      leadAgencyIds: $leadAgencyIds
      companyIds: $companyIds
      planningDate: $planningDate
      search: $search
    )
  }
`

/**
 * Stage-tag facets — the stage tags present on cases matching ALL the OTHER
 * active filters (status + user tag + planning date + search), each with a
 * count. Fully cascaded. Returns `[{ id, count }]` (JSON); zero-match tags are
 * absent. (`caseTagIds` is intentionally NOT passed — it's this facet's own
 * dimension.)
 */
export const CURRENT_CASES_STAGE_TAG_FACETS_QUERY = gql`
  query GetCurrentCasesStageTagFacets(
    $caseStageCategory: String
    $assignedUserIds: [ID!]
    $leadAgencyIds: [ID!]
    $companyIds: [ID!]
    $planningDate: String
    $search: String
  ) {
    getCurrentCasesStageTagFacets(
      caseStageCategory: $caseStageCategory
      assignedUserIds: $assignedUserIds
      leadAgencyIds: $leadAgencyIds
      companyIds: $companyIds
      planningDate: $planningDate
      search: $search
    )
  }
`

/**
 * Assigned-user facets — the users ATTENDING the cases matching ALL the OTHER
 * active filters (status + stage tag + planning date + search), each with a
 * count. Fully cascaded. Returns `[{ id, count }]` (JSON); unassigned cases
 * come back under the sentinel id "unassigned". (`assignedUserIds` is NOT
 * passed — it's this facet's own dimension.)
 */
export const CURRENT_CASES_ASSIGNED_USER_FACETS_QUERY = gql`
  query GetCurrentCasesAssignedUserFacets(
    $caseStageCategory: String
    $caseTagIds: [ID!]
    $leadAgencyIds: [ID!]
    $planningDate: String
    $search: String
  ) {
    getCurrentCasesAssignedUserFacets(
      caseStageCategory: $caseStageCategory
      caseTagIds: $caseTagIds
      leadAgencyIds: $leadAgencyIds
      planningDate: $planningDate
      search: $search
    )
  }
`

/**
 * Planning-date facets — the calendar days that have cases matching ALL the
 * OTHER active filters (status + user tag + stage tag + search), each with a
 * count. Fully cascaded. Returns `[{ date, count }]` (JSON). (`planningDate` is
 * NOT passed — it's this facet's own dimension.)
 */
export const CURRENT_CASES_PLANNING_DATE_FACETS_QUERY = gql`
  query GetCurrentCasesPlanningDateFacets(
    $caseStageCategory: String
    $assignedUserIds: [ID!]
    $caseTagIds: [ID!]
    $leadAgencyIds: [ID!]
    $search: String
  ) {
    getCurrentCasesPlanningDateFacets(
      caseStageCategory: $caseStageCategory
      assignedUserIds: $assignedUserIds
      caseTagIds: $caseTagIds
      leadAgencyIds: $leadAgencyIds
      search: $search
    )
  }
`

/**
 * Lead-agency facets — the lead agencies present on cases matching ALL the OTHER
 * active filters (status + stage tag + user tag + planning date + search), each
 * with a count. Fully cascaded. Returns `[{ id, count }]` (JSON). For company
 * members' lead-agency dropdown. (`leadAgencyIds` is NOT passed — it's this
 * facet's own dimension.)
 */
export const CURRENT_CASES_LEAD_AGENCY_FACETS_QUERY = gql`
  query GetCurrentCasesLeadAgencyFacets(
    $caseStageCategory: String
    $caseTagIds: [ID!]
    $assignedUserIds: [ID!]
    $planningDate: String
    $search: String
  ) {
    getCurrentCasesLeadAgencyFacets(
      caseStageCategory: $caseStageCategory
      caseTagIds: $caseTagIds
      assignedUserIds: $assignedUserIds
      planningDate: $planningDate
      search: $search
    )
  }
`

/**
 * Company facets — the companies present on cases matching ALL the OTHER active
 * filters (status + user tag + planning date + search), each with a count. Fully
 * cascaded (company sits UNDER the planning date, ABOVE the stage tag). Returns
 * `[{ id, count }]` (JSON); zero-match companies are absent. For the SysAdmin /
 * lead-agency company dropdown. (`companyIds` is NOT passed — it's this facet's
 * own dimension.)
 */
export const CURRENT_CASES_COMPANY_FACETS_QUERY = gql`
  query GetCurrentCasesCompanyFacets(
    $caseStageCategory: String
    $assignedUserIds: [ID!]
    $leadAgencyIds: [ID!]
    $planningDate: String
    $search: String
  ) {
    getCurrentCasesCompanyFacets(
      caseStageCategory: $caseStageCategory
      assignedUserIds: $assignedUserIds
      leadAgencyIds: $leadAgencyIds
      planningDate: $planningDate
      search: $search
    )
  }
`

/**
 * Intake stats over a granularity + offset window (or a custom range).
 *
 *   granularity: "weekly" | "monthly" | "yearly" | "custom"
 *   offset:      0 = current period, 1 = last, 2 = last2, 3 = last3
 *   customStart / customEnd: ISO datetimes; required when granularity = "custom"
 *
 * Returns a JSON envelope shaped like:
 *   {
 *     granularity, bucket, rangeStart, rangeEnd,
 *     periods: string[],
 *     leadAgencies: { id, name, shortName }[],
 *     rows: { period, total, byLeadAgency: Record<id, count> }[],
 *     totals: { grand, byLeadAgency: Record<id, count> },
 *     summary: { totalCases, periodsCount, avgPerPeriod, peakPeriod, peakCount }
 *   }
 */
export const CASE_INTAKE_STATS_QUERY = gql`
  query GetCaseIntakeStats(
    $granularity: String!
    $offset: Int
    $customStart: String
    $customEnd: String
    $companyId: ID
    $leadAgencyId: ID
  ) {
    getCaseIntakeStats(
      granularity: $granularity
      offset: $offset
      customStart: $customStart
      customEnd: $customEnd
      companyId: $companyId
      leadAgencyId: $leadAgencyId
    )
  }
`

/**
 * Drill-down for one intake-stats cell: the actual cases behind a
 * `total / won / lost` triple. Same window / scoping args as
 * CASE_INTAKE_STATS_QUERY; `period` narrows to one row (omit for footer
 * totals), `cellCompanyId` / `cellLeadAgencyId` to one column.
 *
 * Returns `{ period, cases: [{ id, motherName, fatherName, otherContactName,
 * childName, outcome, outcomeAt, companyId, leadAgencyId, startedAt }] }`
 * where `outcome` is `active | won | lost` (current state) and `outcomeAt` is
 * when the case reached that state (null for active). Compose the display
 * label with useCaseTitle.
 */
export const CASE_INTAKE_STATS_CASES_QUERY = gql`
  query GetCaseIntakeStatsCases(
    $granularity: String!
    $offset: Int
    $customStart: String
    $customEnd: String
    $companyId: ID
    $leadAgencyId: ID
    $period: String
    $cellCompanyId: ID
    $cellLeadAgencyId: ID
  ) {
    getCaseIntakeStatsCases(
      granularity: $granularity
      offset: $offset
      customStart: $customStart
      customEnd: $customEnd
      companyId: $companyId
      leadAgencyId: $leadAgencyId
      period: $period
      cellCompanyId: $cellCompanyId
      cellLeadAgencyId: $cellLeadAgencyId
    )
  }
`

/**
 * BCBA Report for a single case. Returns a plain-text report (already
 * formatted on the server) scoped to the case's company + lead agency.
 * Meant to be copied to the clipboard by the caller.
 */
export const BCBA_REPORT_QUERY = gql`
  query GetBcbaReport($caseId: ID!, $childFormat: String, $withTags: Boolean, $withAddress: Boolean) {
    getBcbaReport(caseId: $caseId, childFormat: $childFormat, withTags: $withTags, withAddress: $withAddress)
  }
`

/**
 * "BCBA Address" — same structure as the BCBA report, but each case is ONE line:
 * `Contact - Child - address` (no drive time, no tags).
 */
export const BCBA_ADDRESS_REPORT_QUERY = gql`
  query GetBcbaAddressReport($caseId: ID!) {
    getBcbaAddressReport(caseId: $caseId)
  }
`

/**
 * RBT Report — same shape as the BCBA report but grouped by RBT.
 */
export const RBT_REPORT_QUERY = gql`
  query GetRbtReport($caseId: ID!, $caseIds: [ID!]) {
    getRbtReport(caseId: $caseId, caseIds: $caseIds)
  }
`

/**
 * Tiny staff-only projection of a single case. Used by the followups
 * page to refresh BCBA / BCaBA / RBT names after a transfer / staffing
 * change without re-fetching the whole case row.
 *
 * Cheap to run (one row, four small relations) and only fires on
 * actual selection changes, so the cost is bounded.
 */
export const CASE_STAFF_FRAGMENT_QUERY = gql`
  query GetCaseStaffById($id: ID!) {
    case(where: { id: $id }) {
      id
      bcba { id firstName lastName nickName category }
      bcaba { id firstName lastName nickName category }
      rbt { id firstName lastName nickName category }
    }
  }
`

/**
 * Header search. Only the fields the result list actually renders are
 * projected — virtuals like caseProgress / currentStage / unreadTrackingCount
 * each cost one DB round-trip per case and aren't shown in the search
 * dropdown, so we deliberately leave them out.
 */
export const SEARCH_CASES_QUERY = gql`
  query SearchCases($queryString: String!) {
    searchCases(queryString: $queryString) {
      id
      motherName
      fatherName
      otherContactName
      contactCallerId
      childName
      startedAt
      currentStage
      assignedTo {
        id
      }
      company {
        id
        name
        shortName
      }
      leadSource {
        id
        name
        shortName
      }
    }
  }
`
